/**
 * Country → locale configuration.
 *
 * Maps each country to its available locales.
 * When a country has multiple locales, the onboarding wizard shows a locale picker.
 *
 * Generic <V> parameter allows reuse for future languages (e.g., Portuguese: 'pt_BR' | 'pt_PT').
 */

import { createLogger } from '../utils/logger';

const log = createLogger('CountryConfig');

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CountryLocaleConfig<V extends string = string> {
  code: string;           // ISO 3166-1 alpha-2
  emoji: string;
  locales: [V, ...V[]];   // Non-empty tuple — first = default, length > 1 = show picker
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Look up a country config by ISO code. */
export function getCountryConfig(
  code: string,
  countries: CountryLocaleConfig[],
): CountryLocaleConfig | undefined {
  return countries.find((c) => c.code === code);
}

/** Whether a country selection requires a locale picker. */
export function needsLocalePicker(
  code: string,
  countries: CountryLocaleConfig[],
): boolean {
  const config = getCountryConfig(code, countries);
  return config ? config.locales.length > 1 : false;
}

/**
 * All locale options for a country (default first).
 * Unknown countries return an empty array — the unknown is explicit, not
 * papered over with a fabricated locale. Callers already treat "no options"
 * as "hide the variant picker".
 */
export function getLocaleOptions(
  code: string,
  countries: CountryLocaleConfig[],
): string[] {
  const config = getCountryConfig(code, countries);
  if (!config) {
    log.warn('Unknown country code, no locale options', { code });
    return [];
  }
  return config.locales;
}

/**
 * Split countries into a "suggested" section (based on OS locale) and the rest.
 * The detected country is removed from "rest" to avoid duplication.
 * If no match, suggested is empty and rest contains all countries.
 */
export function buildCountryList(
  detectedCode: string | undefined,
  countries: CountryLocaleConfig[],
): {
  suggested: CountryLocaleConfig[];
  rest: CountryLocaleConfig[];
} {
  if (!detectedCode) {
    return { suggested: [], rest: countries };
  }

  const match = countries.find((c) => c.code === detectedCode);
  if (!match) {
    return { suggested: [], rest: countries };
  }

  return {
    suggested: [match],
    rest: countries.filter((c) => c.code !== detectedCode),
  };
}
